import { useMutation, useQueryClient } from '@tanstack/react-query'
import ApiContext from '../../context/ApiContext'
import AuthContext from '../../context/AuthContext'
import { useContext } from 'react'
import { getAuth } from 'firebase/auth'
import { toast } from 'sonner'
import { getCompanySubscriptionQueryKey } from '../queries/useCompanySubscription'
import { getUpcomingInvoiceQueryKey } from '../queries/useUpcomingInvoice'

export type UnsubscribeRequest = {
	subscriptionId: string
}

const useUnsubscribe = () => {
	const auth = getAuth()
	const apiContext = useContext(ApiContext)
	const authContext = useContext(AuthContext)
	const queryClient = useQueryClient()
	const companyId = authContext.company?.id

	const unsubscribe = async (params: UnsubscribeRequest): Promise<void> => {
		if (!companyId) throw new Error('No company selected')

		const idToken = await auth.currentUser?.getIdToken(true)
		const response = await fetch(`${apiContext.apiUrl}/companies/${companyId}/unsubscribe`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${idToken}`,
			},
			body: JSON.stringify(params),
		})

		if (!response.ok) {
			const errorText = await response.text()
			throw new Error(errorText)
		}
	}

	const mutation = useMutation<void, Error, UnsubscribeRequest>({
		mutationFn: unsubscribe,
		onSuccess: () => {
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: getCompanySubscriptionQueryKey(companyId),
					refetchType: 'all',
				}),
				queryClient.invalidateQueries({
					queryKey: getUpcomingInvoiceQueryKey(companyId),
					refetchType: 'all',
				}),
			])
			toast.success('Successfully unsubscribed', {
				description: 'Your subscription has been cancelled and will end at the end of the billing period.',
			})
		},
		onError: (error: Error) => {
			toast.error('Failed to unsubscribe', {
				description: error.message || 'An unexpected error occurred',
			})
		},
	})

	return {
		handleUnsubscribe: mutation.mutateAsync,
		loading: mutation.isPending,
	}
}

export default useUnsubscribe
